import React, { FC, useEffect, useState } from 'react';
import { Button } from 'react-admin';
import { useTranslate } from 'react-admin';
import { TableCell, TextField } from '@material-ui/core';
import { Account } from '~/types';
import { FilterType, TableProps } from '~/types/AccountTableMultiselect';
import AccountsTableDialog from '~/components/AccountTableMultiselect/AccountsTableDialog';
import { AmountField, StatusField } from '~/components';
import onError from '~/errorsHandler';

export interface AccountTableSingleSelectProps {
  resource: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts';
  profileId?: string;
  solutionId?: string;
  label: string;
  selectedAccount?: Account;
  value?: string;
  onChange: (account: Account) => void;
  onClear?: () => void;
  filters?: FilterType[];
  tableProps?: TableProps;
  disabled?: boolean;
  required?: boolean;
  additionalFilters?: { [key: string]: any };
  className?: string;
  style?: React.CSSProperties;
  chipVariant?: 'currency' | 'balance';
  beforeOkCheck?: (selected: Account) => boolean;
  beforeOkErrorMessage?: string;
}

const AccountTableSingleSelect: FC<AccountTableSingleSelectProps> = ({
  resource,
  profileId,
  solutionId,
  label,
  selectedAccount,
  value,
  onChange,
  onClear,
  chipVariant = 'currency',
  filters = ['status', 'number', 'alias', 'currency'],
  tableProps,
  disabled,
  required,
  additionalFilters,
  className,
  style,
  beforeOkCheck,
  beforeOkErrorMessage,
}) => {
  const [open, setOpen] = useState(false);
  const [confirmedAccount, setConfirmedAccount] = useState<Account | undefined>(selectedAccount);
  const [pendingAccount, setPendingAccount] = useState<Account | undefined>();

  // Adopt an externally-known selection (e.g. an existing record being edited).
  useEffect(() => {
    if (selectedAccount) setConfirmedAccount(selectedAccount);
  }, [selectedAccount]);

  // Forget the selection when the caller clears the displayed value.
  useEffect(() => {
    if (!value) setConfirmedAccount(undefined);
  }, [value]);

  // Pre-select the remembered account each time the dialog opens.
  useEffect(() => {
    if (open) setPendingAccount(confirmedAccount);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingId = pendingAccount?.id;
  const translate = useTranslate();
  const t = (key: string): string =>
    translate(`components.ra.fields.AccountTableMultiselect.${key}`);

  const defaultTableProps: TableProps = {
    headers: [t('accountNumber'), t('alias'), t('currency')],
    renderRow: (item: Account) => (
      <>
        <TableCell>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <StatusField status={item.status} variant='dot' />
            {item.number}
          </span>
        </TableCell>
        <TableCell>{item.alias}</TableCell>
        <TableCell>{item.currency}</TableCell>
      </>
    ),
  };

  const defaultTablePropsWithBalance: TableProps = {
    headers: [t('accountNumber'), t('alias'), t('currency'), t('balance')],
    renderRow: (item: Account) => {
      const balance = item.balances?.find(
        (b) => b.code === 'CURRENT_AVAILABLE' || b.code === 'CURRENT_FLOAT'
      )?.amount;
      return (
        <>
          <TableCell>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <StatusField status={item.status} variant='dot' />
              {item.number}
            </span>
          </TableCell>
          <TableCell>{item.alias}</TableCell>
          <TableCell>{item.currency}</TableCell>
          <TableCell align='right'>
            <AmountField amount={balance} />
          </TableCell>
        </>
      );
    },
  };

  const resolvedTableProps =
    tableProps ?? (chipVariant === 'balance' ? defaultTablePropsWithBalance : defaultTableProps);

  const handleRowClick = (item: Account) => {
    setPendingAccount((prev) => (prev?.id === item.id ? undefined : item));
  };

  const handleOk = () => {
    if (pendingAccount) {
      if (beforeOkCheck && !beforeOkCheck(pendingAccount)) {
        onError({ body: { message: beforeOkErrorMessage } });
        return;
      }
      setConfirmedAccount(pendingAccount);
      onChange(pendingAccount);
    } else {
      setConfirmedAccount(undefined);
      onClear?.();
    }
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <TextField
        label={label}
        value={value || ''}
        className={className}
        style={style}
        disabled={disabled}
        required={required}
        inputProps={{ readOnly: true, style: { cursor: disabled ? 'default' : 'pointer' } }}
        InputLabelProps={{ shrink: !!value }}
        onClick={() => !disabled && setOpen(true)}
      />
      <AccountsTableDialog
        resource={resource}
        profileId={profileId}
        solutionId={solutionId}
        open={open}
        onClose={handleClose}
        title={label}
        filters={filters}
        tableProps={resolvedTableProps}
        optionText='number'
        isSelected={(id) => pendingId === id}
        handleToggleSelect={handleRowClick}
        hasSelection={!!pendingId}
        handleDeselectAll={() => setPendingAccount(undefined)}
        selectedItems={pendingAccount ? [pendingAccount] : []}
        handleDeselect={() => setPendingAccount(undefined)}
        chipVariant={chipVariant}
        additionalFilters={additionalFilters}
        selectionControl='radio'
        renderActions={() => (
          <>
            <Button label={t('cancel')} onClick={handleClose} />
            <Button label={t('ok')} onClick={handleOk} />
          </>
        )}
      />
    </>
  );
};

export default AccountTableSingleSelect;
